import { flushSync } from "preact/compat";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { recapLine } from "../../shared/recap-line.ts";
import { useSheetLayer } from "../devsheet-hooks";
import { useScreenCue } from "./ScreenCue";
import { useExit } from "./useExit";
import {
  dismissRecapOffer,
  formatRecapDuration,
  landscapeRoute,
  markRecapSeenHere,
  cachedRecapLink,
  playRecap,
  recapLayers,
  recapLink,
  recapQuality,
  stopRecap,
  subscribeRecap,
  warmRecapLinks,
  type RecapLayers,
  type RecapLinkFetch,
  type RecapPart,
  type RecapRef,
  type RecapSummary,
} from "../recap";

/** The interstitial's and the player's state, from src/recap.ts. */
export function useRecapLayers(): RecapLayers {
  const [layers, setLayers] = useState(recapLayers);
  useEffect(() => subscribeRecap(setLayers), []);
  return layers;
}

const fetchLink: RecapLinkFetch = (id, part) => api.recaps.link(id, part);

/**
 * The signed link (GET /api/recaps/:id/link) for `part` of the recap: the kept one at once when it is
 * fresh, else undefined until the server answers. The video and img elements load these, never the cookie
 * path, since they cannot send x-flag-preview (src/recap.ts).
 */
function useRecapLink(id: string, part: RecapPart): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => cachedRecapLink(id, part) ?? undefined);
  useEffect(() => {
    if (url) return;
    let live = true;
    recapLink(id, part, fetchLink)
      .then((u) => {
        if (live) setUrl(u);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, part]);
  return url;
}

/** Asks for the poster's and this viewport's video link ahead of a tap (the crown card's replay, a Hall month's). */
export function warmRecap(recap: RecapRef): void {
  warmRecapLinks(recap.id, recapQuality(window.innerWidth, window.innerHeight), fetchLink);
}

/** Seen here at once, and on the server when it answers; offline the device mark is enough. */
function markSeen(recap: RecapSummary): void {
  markRecapSeenHere(recap.id);
  void api.recaps.seen(recap.id).catch(() => {});
}

/**
 * Opens the player from a tap. The layer mounts inside the tap (flushSync), so its layout effect
 * starts the video with sound and asks for fullscreen while the press still counts as a gesture.
 */
export function watchRecap(recap: RecapRef): void {
  flushSync(() => playRecap(recap));
}

/**
 * The once-per-recap interstitial (src/recap.ts offerRecap): the poster in the gold frame, the
 * title, the recap's own line (RecapSummary.line, none when it has none) and the running time. One
 * gold primary, Watch recap; Not now (and Escape) marks it seen too, since the Peon of the Month
 * card keeps the replay.
 */
export function RecapIntro({ recap }: { recap: RecapSummary }) {
  const exit = useExit(() => dismissRecapOffer());
  useScreenCue(exit.closing);
  const notNow = () => {
    markSeen(recap);
    exit.close();
  };
  const watch = () => {
    markSeen(recap);
    watchRecap(recap);
  };
  // Escape (and Android back) is Not now; focus trapped, the app behind inert (src/focus-trap.ts).
  const layerRef = useSheetLayer<HTMLDivElement>(!exit.closing, notNow);
  const poster = useRecapLink(recap.id, "poster");
  // The video's link too, so Watch recap opens the player with its source already in hand.
  useEffect(() => warmRecap(recap), [recap.id]);
  // Each month's recap brings its own line (owner, 2026-11-03: "The recap text is wrong on the splash screen").
  const line = recapLine(recap.line);
  return (
    <div class={`recap-intro${exit.cls}`} ref={exit.ref}>
      <div class="recap-intro-card" ref={layerRef} role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby="recap-intro-title" aria-describedby={line ? "recap-intro-line" : undefined}>
        <div class="recap-intro-kicker">Season recap</div>
        <div class="recap-poster-frame">
          {poster ? <img class="recap-poster" src={poster} width={1280} height={720} alt="" /> : <div class="recap-poster" aria-hidden="true" />}
        </div>
        <div class="recap-intro-text">
          <h2 id="recap-intro-title" class="recap-intro-title">{recap.title}</h2>
          {line && (
            <p id="recap-intro-line" class="recap-intro-line">
              {line}
            </p>
          )}
          <p class="recap-intro-duration">{formatRecapDuration(recap.durationS)}</p>
          <div class="recap-intro-actions">
            <button type="button" class="btn btn-primary btn-block" data-autofocus onClick={watch}>
              Watch recap
            </button>
            <button type="button" class="btn-quiet" onClick={notNow}>
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type FullscreenDoc = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
type FullscreenEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type IosVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void; webkitExitFullscreen?: () => void; webkitDisplayingFullscreen?: boolean };
type LockableOrientation = ScreenOrientation & { lock?: (o: string) => Promise<void>; unlock?: () => void };

function fullscreenElement(): Element | null {
  const doc = document as FullscreenDoc;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function leaveFullscreen(): void {
  const doc = document as FullscreenDoc;
  if (!fullscreenElement()) return;
  try {
    if (doc.exitFullscreen) void doc.exitFullscreen().catch(() => {});
    else doc.webkitExitFullscreen?.();
  } catch {
    /* already out */
  }
}

/**
 * The recap itself: the only thing on screen that is not pixel art, in the gold frame on the wood.
 * It plays from the tap with sound and turns sideways the way the browser allows
 * (src/recap.ts landscapeRoute). Leaving fullscreen, the end of the video, Close and Escape all
 * return to the app where it was.
 */
export function RecapPlayer({ recap }: { recap: RecapRef }) {
  const exit = useExit(() => stopRecap());
  useScreenCue(exit.closing);
  const videoRef = useRef<HTMLVideoElement>(null);
  const close = () => {
    leaveFullscreen();
    const video = videoRef.current as IosVideo | null;
    if (video?.webkitDisplayingFullscreen) {
      try {
        video.webkitExitFullscreen?.();
      } catch {
        /* already out */
      }
    }
    video?.pause();
    exit.close();
  };
  const layerRef = useSheetLayer<HTMLDivElement>(!exit.closing, close);
  // Decided once, from the viewport the player opened in.
  const [quality] = useState(() => recapQuality(window.innerWidth, window.innerHeight));
  const src = useRecapLink(recap.id, quality);
  const poster = useRecapLink(recap.id, "poster");
  // The link was usually kept from the interstitial or the crown card; when it arrives after the
  // tap instead, setting it aborts the play() the tap started, so play again once it is in.
  const srcAtOpen = useRef(src);
  useEffect(() => {
    if (!src || src === srcAtOpen.current) return;
    void videoRef.current?.play().catch(() => {});
  }, [src]);

  // Runs inside the tap that opened the layer (watchRecap's flushSync): play with sound, then
  // fullscreen and the landscape lock, or the system player on an iPhone.
  useLayoutEffect(() => {
    const layer = layerRef.current as FullscreenEl | null;
    const video = videoRef.current as IosVideo | null;
    if (!layer || !video) return;
    void video.play().catch(() => {});
    const doc = document as Document & { webkitFullscreenEnabled?: boolean };
    const request = layer.requestFullscreen ?? layer.webkitRequestFullscreen;
    const route = landscapeRoute({
      elementFullscreen: typeof request === "function" && (doc.fullscreenEnabled || !!doc.webkitFullscreenEnabled),
      videoFullscreen: typeof video.webkitEnterFullscreen === "function",
    });
    if (route === "element") {
      try {
        Promise.resolve(request!.call(layer))
          .then(async () => {
            try {
              await (screen.orientation as LockableOrientation | undefined)?.lock?.("landscape");
            } catch {
              /* a desktop or a browser that will not lock: the video still plays */
            }
          })
          .catch(() => {});
      } catch {
        /* refused: the layer plays inline with its Close */
      }
    } else if (route === "video") {
      const enter = () => {
        try {
          video.webkitEnterFullscreen!();
          return true;
        } catch {
          return false;
        }
      };
      // The system player needs the video's size first; a cold video gets it a moment later.
      if (!enter()) video.addEventListener("loadedmetadata", enter, { once: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const video = videoRef.current as IosVideo | null;
    const layer = layerRef.current;
    if (!video || !layer) return;
    let wasFull = false;
    const onChange = () => {
      if (fullscreenElement() === layer) wasFull = true;
      else if (wasFull) close();
    };
    const onEnded = () => close();
    const onIosEnd = () => close();
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    video.addEventListener("ended", onEnded);
    video.addEventListener("webkitendfullscreen", onIosEnd);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("webkitendfullscreen", onIosEnd);
      try {
        (screen.orientation as LockableOrientation | undefined)?.unlock?.();
      } catch {
        /* nothing locked */
      }
      leaveFullscreen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div class={`recap-player${exit.cls}`} ref={(el) => {
      exit.ref.current = el;
      layerRef.current = el;
    }} role="dialog" aria-modal="true" tabIndex={-1} aria-label={`Recap, ${recap.title}`}>
      <div class="recap-player-bar">
        <span class="recap-player-title" aria-hidden="true">{recap.title}</span>
        <button type="button" class="btn-quiet recap-close" onClick={close}>
          Close
        </button>
      </div>
      <div class="recap-video-frame">
        <video
          ref={videoRef}
          class="recap-video"
          src={src}
          poster={poster}
          playsInline
          controls
          preload="auto"
        />
      </div>
    </div>
  );
}
